'use es6';

import { createSelector } from 'reselect';
import { getIsSitePage, getIsBlogListingPage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { createHasScopeSelector, getHasBlogListingPagesWriteScope, getIsUngatedForImmerContentModel } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getPortalHasMembershipsAccess, getHasHidePrimaryCss } from 'page-editor-ui/redux/selectors/authSelectors';
import { getPagePrimaryDomain } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getIsSaveInProgress, getSaveRequested, getHasUnsavedChanges, getWasLastSaveSuccessful, getIsSavePermanentlyDisabled, getLastSaveWasAutoSave } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getContentModel } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getContentModelPageStylesheetsAsJSObject, getContentModelPassword, getContentModelPublicAccessRules, getContentModelPublicAccessRulesEnabled } from 'page-editor-ui/redux/selectors/pageContentModelSelectors';
import { getLayoutSectionsAsJson, getGridLayoutsAsJson, getIsEditingGridLayouts } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getModuleSaveData } from 'ContentEditorUI/redux/selectors/moduleSaveSelectors';
import { getThemePageOverrides } from 'ContentEditorUI/redux/selectors/themeSelectorsAdvanced';
import { isTemporarySlug } from 'ContentEditorUI/utils/contentModelURLUtils';
import { basicSelectorWithStats, makeGatedSelector } from 'ContentEditorUI/redux/selectors/helpers';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getIsDynamicPageSourceValuePortalSpecificCrmObject } from './pageSettingsSelectors';
import { formatCrmObjectDynamicPageDataSourceIdForSave } from '../../utils/dynamicPagesUtils';
import { getContentModelCssAsJSObject } from './pageContentModelSelectors';
import { SaveStates } from 'editor-components/EPConstants';
import { getThemeSaveSuccessful, getThemeSaveInProgress, getThemeLastSaveWasAutoSave } from 'theme-previewer-lib/selectors/themeSettingsMetaSelectors';
export const getHasPermissionsToSaveLandingPages = createHasScopeSelector('landingpages-write');
export const getHasPermissionsToSaveSitePages = createHasScopeSelector('pages-write');
export const getHasPermissionsToSave = createSelector([getIsSitePage, getHasPermissionsToSaveLandingPages, getHasPermissionsToSaveSitePages, getHasBlogListingPagesWriteScope, getIsBlogListingPage], (isSitePage, hasPermissionsToSaveLandingPages, hasPermissionsToSaveSitePages, hasBlogListingPagesWriteScope, isBlogListingPage) => {
  if (isSitePage) {
    return hasPermissionsToSaveSitePages;
  } else if (isBlogListingPage) {
    return hasBlogListingPagesWriteScope;
  }
  return hasPermissionsToSaveLandingPages;
});
export const getIsSavingEnabled = createSelector([getHasPermissionsToSave, getIsSaveInProgress, getIsSavePermanentlyDisabled], (hasPermissionsToSave, isSaveInProgress, isSavePermanentlyDisabled) => hasPermissionsToSave && !isSaveInProgress && !isSavePermanentlyDisabled);

// Fields to subscribe to backbone's change listener
const getImmutableModelDataForBackbone = createSelector([getContentModel, getContentModelPassword, getLayoutSectionsAsJson, getPortalHasMembershipsAccess, getContentModelPublicAccessRules, getContentModelPublicAccessRulesEnabled, getHasHidePrimaryCss, getThemePageOverrides, getIsDynamicPageSourceValuePortalSpecificCrmObject, getContentModelPageStylesheetsAsJSObject, getContentModelCssAsJSObject], (contentModel, contentModelPassword, layoutSectionsAsJson, portalHasMembershipsAccess, contentModelPublicAccessRules, contentModelPublicAccessRulesEnabled, hasHidePrimaryCss, themePageOverrides, isDynamicPageSourceValuePortalSpecificCrmObject, attachedStylesheetsAsJSObject, cssAsJSObject) => {
  const result = {
    attached_stylesheets: attachedStylesheetsAsJSObject,
    campaign: contentModel.get('campaign'),
    campaign_name: contentModel.get('campaignName'),
    campaign_utm: contentModel.get('campaignUtm'),
    css: cssAsJSObject || {},
    css_text: contentModel.get('cssText'),
    domain: contentModel.get('domain'),
    dynamic_page_hub_db_table_id: contentModel.get('dynamicPageHubDbTableId'),
    dynamic_page_data_source_id: formatCrmObjectDynamicPageDataSourceIdForSave(contentModel.get('dynamicPageDataSourceId'), isDynamicPageSourceValuePortalSpecificCrmObject),
    dynamic_page_data_source_type: contentModel.get('dynamicPageDataSourceType'),
    property_for_dynamic_page_slug: contentModel.get('propertyForDynamicPageSlug'),
    property_for_dynamic_page_title: contentModel.get('propertyForDynamicPageTitle'),
    property_for_dynamic_page_meta_description: contentModel.get('propertyForDynamicPageMetaDescription'),
    property_for_dynamic_page_canonical_url: contentModel.get('propertyForDynamicPageCanonicalUrl'),
    property_for_dynamic_page_featured_image: contentModel.get('propertyForDynamicPageFeaturedImage'),
    enable_domain_stylesheets: contentModel.get('enableDomainStylesheets'),
    enable_layout_stylesheets: contentModel.get('enableLayoutStylesheets'),
    featured_image: contentModel.get('featuredImage'),
    featured_image_alt_text: contentModel.get('featuredImageAltText'),
    footer_html: contentModel.get('footerHtml'),
    head_html: contentModel.get('headHtml'),
    html_title: contentModel.get('htmlTitle') || null,
    include_default_custom_css: contentModel.get('includeDefaultCustomCss'),
    language: contentModel.get('language'),
    layout_sections: layoutSectionsAsJson,
    link_rel_canonical_url: contentModel.get('linkRelCanonicalUrl'),
    meta_description: contentModel.get('metaDescription') || null,
    name: contentModel.get('name'),
    slug: contentModel.get('slug') || '/',
    template_path: contentModel.get('templatePath'),
    theme_settings_values: themePageOverrides,
    translated_from_id: contentModel.get('translatedFromId'),
    use_featured_image: contentModel.get('useFeaturedImage')
  };
  if (contentModel.get('requirePassword') && !portalHasMembershipsAccess) {
    result.password = contentModelPassword || null;
  } else {
    result.password = null;
  }
  if (portalHasMembershipsAccess) {
    result.public_access_rules = contentModelPublicAccessRules;
    result.public_access_rules_enabled = contentModelPublicAccessRulesEnabled;
    result.password = contentModelPassword;
  }
  if (hasHidePrimaryCss && result.include_default_custom_css) {
    result.include_default_custom_css = false;
  }
  return result;
});
const getImmerModelDataForBackbone = createSelector([getContentModel, getContentModelPassword, getLayoutSectionsAsJson, getPortalHasMembershipsAccess, getContentModelPublicAccessRules, getContentModelPublicAccessRulesEnabled, getHasHidePrimaryCss, getThemePageOverrides, getIsDynamicPageSourceValuePortalSpecificCrmObject, getContentModelPageStylesheetsAsJSObject, getContentModelCssAsJSObject], (contentModel, contentModelPassword, layoutSectionsAsJson, portalHasMembershipsAccess, contentModelPublicAccessRules, contentModelPublicAccessRulesEnabled, hasHidePrimaryCss, themePageOverrides, isDynamicPageSourceValuePortalSpecificCrmObject, attachedStylesheetsAsJSObject, cssAsJSObject) => {
  const result = {
    attached_stylesheets: attachedStylesheetsAsJSObject,
    campaign: contentModel.campaign,
    campaign_name: contentModel.campaignName,
    campaign_utm: contentModel.campaignUtm,
    css: cssAsJSObject || {},
    css_text: contentModel.cssText,
    domain: contentModel.domain,
    dynamic_page_hub_db_table_id: contentModel.dynamicPageHubDbTableId,
    dynamic_page_data_source_id: formatCrmObjectDynamicPageDataSourceIdForSave(contentModel.dynamicPageDataSourceId, isDynamicPageSourceValuePortalSpecificCrmObject),
    dynamic_page_data_source_type: contentModel.dynamicPageDataSourceType,
    property_for_dynamic_page_slug: contentModel.propertyForDynamicPageSlug,
    property_for_dynamic_page_title: contentModel.propertyForDynamicPageTitle,
    property_for_dynamic_page_meta_description: contentModel.propertyForDynamicPageMetaDescription,
    property_for_dynamic_page_canonical_url: contentModel.propertyForDynamicPageCanonicalUrl,
    property_for_dynamic_page_featured_image: contentModel.propertyForDynamicPageFeaturedImage,
    enable_domain_stylesheets: contentModel.enableDomainStylesheets,
    enable_layout_stylesheets: contentModel.enableLayoutStylesheets,
    featured_image: contentModel.featuredImage,
    featured_image_alt_text: contentModel.featuredImageAltText,
    footer_html: contentModel.footerHtml,
    footer_template_path: contentModel.footerTemplatePath,
    footer_variant_name: contentModel.footerVariantName,
    head_html: contentModel.headHtml,
    header_template_path: contentModel.headerTemplatePath,
    header_variant_name: contentModel.headerVariantName,
    html_title: contentModel.htmlTitle || null,
    include_default_custom_css: contentModel.includeDefaultCustomCss,
    language: contentModel.language,
    layout_sections: layoutSectionsAsJson,
    link_rel_canonical_url: contentModel.linkRelCanonicalUrl,
    meta_description: contentModel.metaDescription || null,
    name: contentModel.name,
    slug: contentModel.slug || '/',
    template_path: contentModel.templatePath,
    theme_settings_values: themePageOverrides,
    translated_from_id: contentModel.translatedFromId,
    use_featured_image: contentModel.useFeaturedImage
  };
  if (contentModel.requirePassword && !portalHasMembershipsAccess) {
    result.password = contentModelPassword || null;
  } else {
    result.password = null;
  }
  if (portalHasMembershipsAccess) {
    result.public_access_rules = contentModelPublicAccessRules;
    result.public_access_rules_enabled = contentModelPublicAccessRulesEnabled;
    result.password = contentModelPassword;
  }
  if (hasHidePrimaryCss && result.include_default_custom_css) {
    result.include_default_custom_css = false;
  }
  return result;
});
const getModelDataForBackbone = makeGatedSelector(getIsUngatedForImmerContentModel, getImmutableModelDataForBackbone, getImmerModelDataForBackbone);

// Save data minus all widget data
const _getModelSaveData = createSelector([getModelDataForBackbone, getPagePrimaryDomain], (modelData, primaryDomain) => {
  if (modelData.domain === primaryDomain) {
    modelData.domain = '';
  }
  return modelData;
});
export const getModelSaveData = basicSelectorWithStats((state, opts = {}) => {
  const modelSaveData = _getModelSaveData(state);
  const {
    slug
  } = modelSaveData;
  if (opts.isSaveBeforePublish && isTemporarySlug(slug)) {
    modelSaveData.slug = slug.substring(0, slug.indexOf('-temporary-slug-'));
  }
  return modelSaveData;
});

// All save data
export const getNonGridsSaveData = createSelector([getModelSaveData, getModuleSaveData], (modelSaveData, moduleSaveData) => Object.assign({}, modelSaveData, moduleSaveData));
export const getCanSave = createSelector([getHasPermissionsToSave, getIsSavePermanentlyDisabled, getIsReadOnlyMode], (hasPermissionsToSave, isSavePermanentlyDisabled, isReadOnly) => hasPermissionsToSave && !isSavePermanentlyDisabled && !isReadOnly);

//TODO- consider moving this to CEUI when other editor(s) supports universal theme editor
export const getUnifiedSaveState = createSelector([getIsSaveInProgress, getThemeSaveInProgress, getSaveRequested, getHasUnsavedChanges, getWasLastSaveSuccessful, getThemeSaveSuccessful, getLastSaveWasAutoSave, getThemeLastSaveWasAutoSave], (isSaveInProgress, themeSettingsSaveInProgress, saveRequested, hasUnsavedChanges, wasLastSaveSuccessful, themeSettingsSaveSuccess, lastSaveWasAutoSave, themeLastSaveWasAutoSave) => {
  if (isSaveInProgress || themeSettingsSaveInProgress) {
    return SaveStates.SAVING_CHANGES;
  }
  if (hasUnsavedChanges) {
    return SaveStates.UNSAVED_CHANGES;
  }
  if (!saveRequested) {
    return SaveStates.NO_UNSAVED_CHANGES;
  }
  if (wasLastSaveSuccessful || themeSettingsSaveSuccess) {
    if (lastSaveWasAutoSave || themeLastSaveWasAutoSave) {
      return SaveStates.AUTO_SAVED_CHANGES;
    } else {
      return SaveStates.SAVED_CHANGES;
    }
  }
  return SaveStates.SAVE_FAILED;
});

// CamelCase versions that preserve original camelCase format from Redux state
const getImmerModelDataForBackboneCamelCase = createSelector([getContentModel, getContentModelPassword, getLayoutSectionsAsJson, getPortalHasMembershipsAccess, getContentModelPublicAccessRules, getContentModelPublicAccessRulesEnabled, getHasHidePrimaryCss, getThemePageOverrides, getIsDynamicPageSourceValuePortalSpecificCrmObject, getContentModelPageStylesheetsAsJSObject, getContentModelCssAsJSObject], (contentModel, contentModelPassword, layoutSectionsAsJson, portalHasMembershipsAccess, contentModelPublicAccessRules, contentModelPublicAccessRulesEnabled, hasHidePrimaryCss, themePageOverrides, isDynamicPageSourceValuePortalSpecificCrmObject, attachedStylesheetsAsJSObject, cssAsJSObject) => {
  const result = Object.assign({}, contentModel, {
    // Override specific properties that need transformation
    attachedStylesheets: attachedStylesheetsAsJSObject,
    css: cssAsJSObject || {},
    htmlTitle: contentModel.htmlTitle || null,
    metaDescription: contentModel.metaDescription || null,
    slug: contentModel.slug || '/',
    layoutSections: layoutSectionsAsJson,
    themeSettingsValues: themePageOverrides,
    dynamicPageDataSourceId: formatCrmObjectDynamicPageDataSourceIdForSave(contentModel.dynamicPageDataSourceId, isDynamicPageSourceValuePortalSpecificCrmObject)
  });
  if (contentModel.requirePassword && !portalHasMembershipsAccess) {
    result.password = contentModelPassword || null;
  } else {
    result.password = null;
  }
  if (portalHasMembershipsAccess) {
    result.publicAccessRules = contentModelPublicAccessRules;
    result.publicAccessRulesEnabled = contentModelPublicAccessRulesEnabled;
    result.password = contentModelPassword;
  }
  if (hasHidePrimaryCss && result.includeDefaultCustomCss) {
    result.includeDefaultCustomCss = false;
  }
  return result;
});
const getImmutableModelDataForBackboneCamelCase = createSelector([getContentModel, getContentModelPassword, getLayoutSectionsAsJson, getPortalHasMembershipsAccess, getContentModelPublicAccessRules, getContentModelPublicAccessRulesEnabled, getHasHidePrimaryCss, getThemePageOverrides, getIsDynamicPageSourceValuePortalSpecificCrmObject, getContentModelPageStylesheetsAsJSObject, getContentModelCssAsJSObject], (contentModel, contentModelPassword, layoutSectionsAsJson, portalHasMembershipsAccess, contentModelPublicAccessRules, contentModelPublicAccessRulesEnabled, hasHidePrimaryCss, themePageOverrides, isDynamicPageSourceValuePortalSpecificCrmObject, attachedStylesheetsAsJSObject, cssAsJSObject) => {
  const result = Object.assign({}, contentModel.toJS(), {
    // Override specific properties that need transformation
    attachedStylesheets: attachedStylesheetsAsJSObject,
    css: cssAsJSObject || {},
    htmlTitle: contentModel.get('htmlTitle') || null,
    metaDescription: contentModel.get('metaDescription') || null,
    slug: contentModel.get('slug') || '/',
    layoutSections: layoutSectionsAsJson,
    themeSettingsValues: themePageOverrides,
    dynamicPageDataSourceId: formatCrmObjectDynamicPageDataSourceIdForSave(contentModel.get('dynamicPageDataSourceId'), isDynamicPageSourceValuePortalSpecificCrmObject)
  });
  if (contentModel.get('requirePassword') && !portalHasMembershipsAccess) {
    result.password = contentModelPassword || null;
  } else {
    result.password = null;
  }
  if (portalHasMembershipsAccess) {
    result.publicAccessRules = contentModelPublicAccessRules;
    result.publicAccessRulesEnabled = contentModelPublicAccessRulesEnabled;
    result.password = contentModelPassword;
  }
  if (hasHidePrimaryCss && result.includeDefaultCustomCss) {
    result.includeDefaultCustomCss = false;
  }
  return result;
});
const getModelDataForBackboneCamelCase = makeGatedSelector(getIsUngatedForImmerContentModel, getImmutableModelDataForBackboneCamelCase, getImmerModelDataForBackboneCamelCase);
const _getModelSaveDataCamelCase = createSelector([getModelDataForBackboneCamelCase, getPagePrimaryDomain], (modelData, primaryDomain) => {
  if (modelData.domain === primaryDomain) {
    modelData.domain = '';
  }
  return modelData;
});
export const getModelSaveDataCamelCase = basicSelectorWithStats((state, opts = {}) => {
  const modelSaveData = _getModelSaveDataCamelCase(state);
  const {
    slug
  } = modelSaveData;
  if (opts.isSaveBeforePublish && isTemporarySlug(slug)) {
    modelSaveData.slug = slug.substring(0, slug.indexOf('-temporary-slug-'));
  }
  return modelSaveData;
});
export const getModuleSaveDataCamelCase = createSelector([getModuleSaveData], moduleSaveData => ({
  widgets: moduleSaveData.widgets,
  widgetContainers: moduleSaveData.widget_containers,
  widgetsInRichText: moduleSaveData.widgets_in_rich_text
}));
export const getSaveDataCamelCase = createSelector([getModelSaveDataCamelCase, getModuleSaveDataCamelCase], (modelSaveData, moduleSaveData) => Object.assign({}, modelSaveData, moduleSaveData));
export const getGridLayoutSaveData = createSelector([getSaveDataCamelCase, getGridLayoutsAsJson], (saveDataCamelCase, gridLayoutsAsJson) => {
  return {
    content: saveDataCamelCase,
    gridLayouts: gridLayoutsAsJson
  };
});
export const getSaveData = makeGatedSelector(getIsEditingGridLayouts, getNonGridsSaveData, getGridLayoutSaveData);